import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentProps,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { useTranslation } from "react-i18next";
import {
  type Draft,
  type Exercise,
  type UnitsPreference,
} from "@jackedlogs/shared";

import type { ConfirmFn, DurableWriteFn, NotifyFn } from "../../seams";
import type { LoggerProfile } from "../../Logger/hostDeps";
import type { useTheme } from "../../Logger/theme";
import type {
  TimelineFocusSectionInput,
  TimelineLoggerInput,
} from "../../Logger/loggerContext";
import {
  PRESCRIBE_HOLD,
  PRESCRIBE_REST_ADVANCE,
  PRESCRIBE_REST_PICKER,
  PRESCRIBE_REST_TIMER,
} from "./prescribeIdleDeps";
import {
  applyLoggerAction,
  applyPrescribeEffort,
  resolvePrescribeAddr,
  type PrescribeAddrCtx,
} from "./prescribeDispatch";
import { usePrescribeSetCommands } from "./usePrescribeSetCommands";
import {
  buildLoggerModalsProps,
  usePrescribeModalState,
} from "./prescribeModalState";
import type {
  DraftEntry,
  LoggerAction,
  LoggerDraft,
  WeightUnit,
} from "../../Logger/state/loggerDraftTypes";
import { slotDraftToEntry } from "../../Logger/slotDraftHydration";
import { useTimelineLogger } from "../../Logger/useTimelineLogger";
import { TimelineFocusSection } from "../../Logger/TimelineFocusSection";
import { useEffortCommit } from "../../Logger/useEffortCommit";
import { usePlateCalc } from "../../Logger/usePlateCalc";
import { useTimerDefaults } from "../../Logger/useTimerDefaults";
import { useSetCyclePill } from "../../Logger/useSetCyclePill";
import { useExerciseNameBySlug } from "../../Logger/exerciseName";
import { buildGroupCatalog, groupDisplayLabel } from "../../Logger/loggerUnits";

export type PrescribeMountArgs = {
  /** The composer's own live Draft (useProgramEditor/useComposer) — the ONE
   *  source of truth. Mapped through slotDraftToEntry, never copied. */
  programDraft: Draft;
  /** The composer's setDraft (useProgramEditor, exposed via ...editor). The
   *  K2.2 adapter applies the shared draft-direct actions through it — one
   *  save-path, the composer's own Draft. */
  setDraft: Dispatch<SetStateAction<Draft>>;
  /** The FULL exercise catalog (ProgramComposerProps.exerciseCatalog) —
   *  the only composer prop shaped like the Logger's `Exercise[]`
   *  (`catalog: CatalogItem[]` is a lossy, one-way name-only projection and
   *  cannot serve the card). */
  exerciseCatalog: ReadonlyArray<Exercise> | undefined;
  /** The unit the DRAFT cells were seeded in (composer.cellUnit) — NEVER the
   *  athlete's display preference, or the two directions disagree (see
   *  slotDraftHydration.ts). */
  cellUnit: UnitsPreference;
  notify: NotifyFn;
  confirm: ConfirmFn;
  profile: LoggerProfile;
  t: ReturnType<typeof useTranslation>["t"];
  i18n: ReturnType<typeof useTranslation>["i18n"];
  theme: ReturnType<typeof useTheme>;
};

/** PE-22 K2.1 — assembles the narrow bags `TimelineFocusSection` /
 *  `useTimelineLogger` read (loggerContext.ts's `Pick` lists), from the
 *  composer's own Draft instead of a live logging session.
 *
 *  Owns: `selectedDay`/`selectedWeek` (the day×week cursor K2.3's matrix
 *  will drive), the focus cursor (`activeId` et al.), and the modal/UI
 *  state the bags require. `dispatch` is a NO-OP STUB — K2.2 replaces it
 *  with the draft-direct adapter over `setDraft`/`programEditActions`, so
 *  this tree renders strictly read-only for now.
 *
 *  ⭐ THE NON-ISTANZIA MACHINERY (cycleSetPill/plate/timerDefaults vs.
 *  restTimer/restPicker/restAdvance/hold): the narrowed bags still NAME
 *  these fields because the card reads their SHAPE, but every path that
 *  would actually RUN one is mode-gated off in prescribe already
 *  (useTimelineLogger.ts:271 gates the rest transition on `mode !== 'log'`;
 *  useSetCyclePill.ts:203-212 never mode==='prescribe'-dispatches a
 *  completion; useEffortCommit's onFail returns immediately on
 *  mode==='prescribe'). Two different answers follow from that:
 *   - plate / timerDefaults / cycleSetPill take no LIVE session as input at
 *     all (no restTimer-that-must-be-ticking, no in-progress clock) — they
 *     are called here exactly as useLoggerScreen calls them, for real.
 *   - restTimer / restPicker / restAdvance / hold exist ONLY to run a live
 *     countdown a real session owns. Instantiating the real hooks here
 *     would start machinery this mount has no session to run (and no
 *     `timerNotification`/`patchProfile` seam to back it). So each is a
 *     REAL value of the hook's own return type, sitting at rest — never
 *     started, because nothing on this screen ever starts it. Not a cast,
 *     not `as never`: every field is the type's own idle state
 *     (`running: false`, no-op callbacks). */
export function usePrescribeMount(args: PrescribeMountArgs) {
  const {
    programDraft,
    setDraft,
    exerciseCatalog,
    cellUnit,
    notify,
    confirm,
    profile,
    t,
    i18n,
    theme,
  } = args;

  // ⛔ PE-22 K2.1 — prescribe AUTHORING has NO durable-write authority. The
  // only writer the mount ever wired was useTimerDefaults' per-exercise PATCH
  // (`saveExerciseTimerDefaults`), reachable via the card's timer-settings
  // affordance — a program-editor must never PATCH an exercise's live defaults
  // (NON ISTANZIA / display-only). So the mount takes no `durableWrite` at all
  // and hands useTimerDefaults a fail-closed one: a reach fails loudly, never
  // silently persists. (The affordance itself is gated off in K2.2, with the
  // other inert controls; this is the data-safety floor under that.) K2.1
  // review, jl-code-standards-auditor High.
  const noPersist: DurableWriteFn = async () => ({
    outcome: "rejected",
    error: new Error("prescribe authoring has no write authority"),
  });

  // --- the day×week cursor (K2.3's matrix drives this; K2.1 just owns it) --
  const [selectedDay, setSelectedDay] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState(0);

  // --- catalog: the composer only carries CatalogItem[] (slug+name); the
  // card needs the FULL Exercise[] row, which is exerciseCatalog. Kept as
  // local state (not a raw prop pass-through) because timerDefaults writes
  // an optimistic PATCH result back into it, exactly like useLoggerCatalog. -
  const [catalog, setCatalog] = useState<Exercise[] | null>(() =>
    exerciseCatalog ? [...exerciseCatalog] : null,
  );
  // Re-seed when the host hands us a fresh catalog reference (the host's own
  // fetch/refresh cadence) — timerDefaults' optimistic write still wins
  // between refreshes, same as the real Logger's catalog mirror.
  useEffect(() => {
    if (exerciseCatalog) setCatalog([...exerciseCatalog]);
  }, [exerciseCatalog]);

  const getExerciseNameBySlug = useExerciseNameBySlug(catalog);

  // A prescription has no session, so there is nothing real to timestamp —
  // but LoggerDraft.startedAt is required by the type, and every reader of
  // it (useSetCyclePill's 'log-past' branch) is mode-gated off in prescribe.
  // One mount-time ISO string, never re-read for meaning.
  const [startedAt] = useState(() => new Date().toISOString());

  // The card renders in the athlete's OWN preference (LoggerProfile), same
  // source useLoggerScreen's `defaultUnit` reads — NOT `cellUnit` (see
  // PrescribeMountArgs.cellUnit doc).
  const defaultUnit = profile.unitsPreference;
  const displayUnit: WeightUnit = defaultUnit === "lb" ? "lb" : "kg";

  // --- K1 read adapter: the selected session's slots, at the selected week,
  // as the card's DraftEntry[]. Filters the `null`s (inactive weeks). ------
  const draft: LoggerDraft = useMemo(() => {
    const session = programDraft.sessions[selectedDay];
    const slots = session?.slots ?? [];
    const entries: DraftEntry[] = [];
    for (const slot of slots) {
      const exerciseRow = catalog?.find((c) => c.slug === slot.exerciseId);
      const entry = slotDraftToEntry(slot, selectedWeek, {
        exercise: exerciseRow
          ? {
              name: getExerciseNameBySlug(exerciseRow.slug, slot.exerciseName),
              isBodyweight: exerciseRow.isBodyweight,
              movementPattern: exerciseRow.movementPattern,
              isTimeBased: exerciseRow.isTimeBased,
              unilateral: exerciseRow.unilateral,
            }
          : undefined,
        cellUnit,
        displayUnit,
      });
      if (entry) entries.push(entry);
    }
    return { startedAt, entries };
  }, [
    programDraft,
    selectedDay,
    selectedWeek,
    catalog,
    cellUnit,
    displayUnit,
    startedAt,
    getExerciseNameBySlug,
  ]);

  // PE-22 K2.2 — the draft-direct adapter. Each card action becomes a shared
  // programEditActions write, applied through the composer's setDraft; the card
  // never gets a second reducer. ⭐ addrCtx carries only the CURSOR (day/week):
  // the slot is resolved from the live `d` inside setDraft, so two slot-count-
  // changing actions before a re-render can't address a stale index.
  const addrCtx: PrescribeAddrCtx = useMemo(
    () => ({ sIdx: selectedDay, weekIdx: selectedWeek }),
    [selectedDay, selectedWeek],
  );
  const dispatch = useCallback<Dispatch<LoggerAction>>(
    (action) => setDraft((d) => applyLoggerAction(d, action, addrCtx)),
    [setDraft, addrCtx],
  );

  // --- the focus cursor + modal/UI state the bags require -----------------
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedSetIdx, setSelectedSetIdx] = useState<number | null>(null);
  const [rosterExpanded, setRosterExpanded] = useState(true);
  // PE-26 — K2.1's "READ-ONLY" note above no longer holds. usePrescribeModalState
  // owns the six affordances' open/closed state (picker / effort-scale / tip /
  // swap / group-picker / superset-menu) AND the add-exercise wiring off this
  // mount's own dispatch; PrescribeTree mounts <LoggerModals> from
  // `loggerModalsProps` below. The card's ＋/swap/tip/group/superset taps open
  // real modals now, not blind setters.
  const modalState = usePrescribeModalState({
    draft,
    dispatch,
    defaultUnit,
    catalog,
    profile,
  });
  const {
    setPickerOpen,
    setEffortScaleOpen,
    setTipEntryIdx,
    setSwapEntryIdx,
    setGroupPickerEntryIdx,
    setSupersetMenu,
  } = modalState;

  const groupCatalog = useMemo(() => buildGroupCatalog(draft.entries), [draft.entries]);
  const groupLabelFor: TimelineFocusSectionInput["groupLabelFor"] = useCallback(
    (group) => groupDisplayLabel(group, groupCatalog, t),
    [groupCatalog, t],
  );

  // ⛔ NON ISTANZIA — idle REAL values, extracted to prescribeIdleDeps.ts
  // (referentially stable module consts; that file's header explains why each
  // is the hook's own idle state, gated from use, and not a cast).
  const restTimer = PRESCRIBE_REST_TIMER;
  const restPicker = PRESCRIBE_REST_PICKER;
  const restAdvance = PRESCRIBE_REST_ADVANCE;
  const hold = PRESCRIBE_HOLD;

  // ✅ Pure/cheap — called exactly like useLoggerScreen calls them. ---------
  const cycleSetPill = useSetCyclePill({
    entries: draft.entries,
    dispatch,
    // No PR history / stored maxes exist for a prescription — `null` is the
    // same "not loaded yet" fallback the real hook already defends against.
    prHistory: null,
    currentMaxes: null,
    catalog,
    defaultRestSec: profile.defaultRestSec,
    restTimer,
    startedAt: draft.startedAt,
    mode: "prescribe",
  });

  const plate = usePlateCalc({ entries: draft.entries, catalog, dispatch });

  const timerDefaults = useTimerDefaults({
    catalog,
    setCatalog,
    profileDefaultRestSec: profile.defaultRestSec,
    profileDefaultDurationSec: profile.defaultDurationSec,
    t,
    durableWrite: noPersist,
    notify,
  });

  // --- the narrow bags themselves ------------------------------------------
  const timelineLoggerCtx: TimelineLoggerInput = {
    draft,
    dispatch,
    t,
    profile,
    getExerciseNameBySlug,
    catalog,
    defaultUnit,
    cycleSetPill,
    mode: "prescribe",
    restTimer,
    restPicker,
    restAdvance,
    activeId,
    setActiveId,
    selectedSetIdx,
    setSelectedSetIdx,
    rosterExpanded,
    setRosterExpanded,
    deps: { confirm },
  };

  const tl = useTimelineLogger(timelineLoggerCtx);

  const focusSectionCtx: TimelineFocusSectionInput = {
    draft,
    dispatch,
    theme,
    t,
    i18n,
    getExerciseNameBySlug,
    groupLabelFor,
    defaultUnit,
    setPickerOpen,
    setEffortScaleOpen,
    setTipEntryIdx,
    setSwapEntryIdx,
    setGroupPickerEntryIdx,
    setSupersetMenu,
    plate,
    hold,
    timerDefaults,
    mode: "prescribe",
    deps: { confirm },
  };

  // PE-22 K2.2 — an effort-band tap in prescribe writes the prescription: the
  // row's kind becomes 'rir' and the value lands on the SlotDraft cell (never a
  // DraftSet, reperto 6). onPrescribeEffort closes over the CURRENT bound entry/
  // set (tl.*), resolved to the program-Draft address at tap time.
  const effort = useEffortCommit({
    boundSet: tl.boundSet,
    mode: "prescribe",
    activeEntryIdx: tl.activeEntryIdx,
    boundSetIdx: tl.boundSetIdx,
    onPrescribeEffort: (rir) =>
      setDraft((d) =>
        applyPrescribeEffort(
          d,
          resolvePrescribeAddr(d, addrCtx, tl.activeEntryIdx, tl.boundSetIdx),
          rir,
        ),
      ),
    dispatch,
    onLogSet: tl.onLogSet,
    onLogFail: tl.onLogFail,
    onToggleFail: tl.onToggleFail,
    captureTimer: () => hold.capture(tl.activeEntryIdx, tl.boundSetIdx),
    onOpenEffortScale: () => setEffortScaleOpen(true),
    willPromptAmrap: tl.amrapConfirm.shouldPrompt,
  });

  // PE-22 K3.2/K3.3/K3.4 — the bound set's prescription commands beyond reps
  // and effort: load-axis, "apply to all sets", "A tempo"/"Nota". Extracted
  // to its own hook (usePrescribeMount hit the 500-line cap exactly at
  // K3.4) — same addrCtx/tl this mount already built.
  const { loadAxis, onApplyToAllSets, timedChip, noteChip } =
    usePrescribeSetCommands(setDraft, addrCtx, tl, confirm, t);

  // A prescribed set never carries a completedAt (measurement-contract §5),
  // so this is always false while the day has entries — a real answer, not a
  // stand-in: nothing on this screen can ever complete a set.
  const allLogged =
    draft.entries.length > 0 &&
    draft.entries.every(
      (e) => e.sets.length > 0 && e.sets.every((s) => s.completedAt !== null),
    );
  const restFrozen = tl.rest !== null;

  // PE-22 K2.3 — the focused exercise's SLUG, for the block-placement seam
  // (usePrescribePlacement). `activeId` is the card's focus cursor, which is
  // the entry's `entryId` — `slotDraftToEntry`'s contract makes that
  // `entryId === slot.id` (see the K1 read adapter above), so it resolves
  // back to a slot, and the slot names the exercise. Not exposed as a whole
  // Exercise row — the placement seam only needs the id to match against
  // ComposedBlock.members[].exerciseId.
  const activeExerciseId = useMemo(() => {
    if (activeId === null) return null;
    const session = programDraft.sessions[selectedDay];
    return session?.slots.find((sl) => sl.id === activeId)?.exerciseId ?? null;
  }, [activeId, programDraft, selectedDay]);

  // PE-26 — ⭐ THE EXIT IS THE GESTURE (Antenore, 27/08): tapping the exercise
  // that is already open closes it, and the matrix falls back to being the
  // plain day/week cursor — the state his flow describes as "a nessun esercizio
  // selezionato la matrice è il cursore". Removing PE-25's Done button without
  // this left NO way back to that state, for touch or for a screen reader
  // (`focusRow` only ever takes a concrete id); the a11y review caught it.
  // ⛔ Prescribe only, deliberately: in the logger `focusRow` is how you move
  // between exercises mid-set, and a toggle there would close the card you just
  // tapped by mistake.
  const focusRowOrClose = useCallback(
    (id: string) => {
      if (id === activeId) tl.closeCard();
      else tl.focusRow(id);
    },
    [activeId, tl],
  );

  const focusSectionProps: ComponentProps<typeof TimelineFocusSection> = {
    ctx: focusSectionCtx,
    tl: { ...tl, focusRow: focusRowOrClose },
    // ME-3's in-workout target readout reads a live session's own totals —
    // nothing has been performed yet in a prescription, so there is nothing
    // to project. A real empty state, not a placeholder for wiring later.
    targetRows: [],
    effort,
    // Coach cues resolve against a STARTED plan/program instance
    // (usePlanCues keys on fromPlanId); the composer draft being AUTHORED
    // has no such instance yet.
    activeCues: undefined,
    allLogged,
    restFrozen,
  };

  // PE-26 — everything PrescribeTree needs to mount <LoggerModals>, assembled
  // in prescribeModalState.ts (the modal-state hook's values/setters + this
  // mount's own dispatch/plate/timerDefaults/groupCatalog/idle restPicker).
  // ExercisePickerMount / ExerciseTimerSheetMount / EffortScalePicker / theme
  // / t / profile / confirm / notify are NOT in here — PrescribeTree already
  // has those as its own props and spreads them alongside this object.
  const loggerModalsProps = buildLoggerModalsProps(modalState, {
    catalog,
    entries: draft.entries,
    dispatch,
    plate,
    restPicker,
    timerDefaults,
    groupCatalog,
    defaultUnit,
  });

  return {
    focusSectionProps,
    selectedDay,
    setSelectedDay,
    selectedWeek,
    setSelectedWeek,
    activeExerciseId,
    getExerciseNameBySlug,
    // PE-22 K3.2/K3.3/K3.4 — data for PrescribeTree to build
    // <LoadAxisSelector>, the "apply to all sets" button, and the "A
    // tempo"/"Nota" chips. A .ts hook can't hold JSX itself; the elements are
    // assembled in the .tsx mount (PrescribeTree.tsx / FocusPanelChipRow.tsx).
    loadAxis,
    onApplyToAllSets,
    timedChip,
    noteChip,
    loggerModalsProps,
  };
}
